import { Stack } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";
import Header from "./components/Header";
import Content from "./components/Content";
import { useGetChargesQuery } from "src/api/services/chargesApi";
import { ProductCategories } from "src/api/types";

interface Props {
  page: string;
}

export type FaqsProps = {
  section: string;
  param: string;
  faqs: {
    question: string;
    answer: string;
  }[];
};

const FrequentlyAskedQuestions = ({ page }: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayAnswer, setDisplayAnwer] = useState<string | null>(null);
  const glp1WeightLossSubscription = usePatientSubscriptions(
    (subs) =>
      subs.find((s) => s.product_category.name === ProductCategories.WeightLoss)
        ?.product.is_glp1,
  );
  const { data: charges = [] } = useGetChargesQuery();

  const glp1Charge = useMemo(
    () =>
      charges.find(
        (c) => c.product_category.name === ProductCategories.WeightLoss,
      )?.product.is_glp1,
    [charges],
  );

  const section = useMemo(() => {
    return searchParams.get("section") as string;
  }, [searchParams]);

  const faqs: FaqsProps[] = useMemo(
    () => [
      {
        section: `${page}.faqs.0.section`,
        param: `${page}.faqs.0.param`,
        faqs: [
          {
            question: `${page}.faqs.0.faqs.0.question`,
            answer: `${page}.faqs.0.faqs.0.answer`,
          },
          {
            question: `${page}.faqs.0.faqs.1.question`,
            answer: `${page}.faqs.0.faqs.1.answer`,
          },
          {
            question: `${page}.faqs.0.faqs.2.question`,
            answer: `${page}.faqs.0.faqs.2.answer`,
          },
          {
            question: `${page}.faqs.0.faqs.3.question`,
            answer: `${page}.faqs.0.faqs.3.answer`,
          },
        ],
      },
      {
        section: `${page}.faqs.1.section`,
        param: `${page}.faqs.1.param`,
        faqs: [
          {
            question: `${page}.faqs.1.faqs.0.question`,
            answer: `${page}.faqs.1.faqs.0.answer`,
          },
          {
            question: `${page}.faqs.1.faqs.1.question`,
            answer: `${page}.faqs.1.faqs.1.answer`,
          },
          {
            question: `${page}.faqs.1.faqs.2.question`,
            answer: `${page}.faqs.1.faqs.2.answer`,
          },
          {
            question: `${page}.faqs.1.faqs.3.question`,
            answer: `${page}.faqs.1.faqs.3.answer`,
          },
          {
            question: `${page}.faqs.1.faqs.4.question`,
            answer: `${page}.faqs.1.faqs.4.answer`,
          },
          {
            question: `${page}.faqs.1.faqs.5.question`,
            answer: `${page}.faqs.1.faqs.5.answer`,
          },
        ],
      },
      {
        section: `${page}.faqs.2.section`,
        param: `${page}.faqs.2.param`,
        faqs: [
          {
            question: `${page}.faqs.2.faqs.0.question`,
            answer: `${page}.faqs.2.faqs.0.answer`,
          },
          {
            question: `${page}.faqs.2.faqs.1.question`,
            answer: `${page}.faqs.2.faqs.1.answer`,
          },
          {
            question: `${page}.faqs.2.faqs.2.question`,
            answer: `${page}.faqs.2.faqs.2.answer`,
          },
          {
            question: `${page}.faqs.2.faqs.3.question`,
            answer: `${page}.faqs.2.faqs.3.answer`,
          },
          {
            question: `${page}.faqs.2.faqs.4.question`,
            answer: `${page}.faqs.2.faqs.4.answer`,
          },
        ],
      },
      {
        section: `${page}.faqs.3.section`,
        param: `${page}.faqs.3.param`,
        faqs: [
          {
            question: `${page}.faqs.3.faqs.0.question`,
            answer: `${page}.faqs.3.faqs.0.answer`,
          },
          {
            question: `${page}.faqs.3.faqs.1.question`,
            answer: `${page}.faqs.3.faqs.1.answer`,
          },
          {
            question: `${page}.faqs.3.faqs.2.question`,
            answer: `${page}.faqs.3.faqs.2.answer`,
          },
          {
            question: `${page}.faqs.3.faqs.3.question`,
            answer: `${page}.faqs.3.faqs.3.answer`,
          },
          {
            question: `${page}.faqs.3.faqs.4.question`,
            answer: `${page}.faqs.3.faqs.4.answer`,
          },
          {
            question: `${page}.faqs.3.faqs.5.question`,
            answer: `${page}.faqs.3.faqs.5.answer`,
          },
          {
            question: `${page}.faqs.3.faqs.6.question`,
            answer: `${page}.faqs.3.faqs.6.answer`,
          },
        ],
      },
    ],
    [page],
  );

  const handleSetSearchParam = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("section", value);
        return params;
      });
    },
    [setSearchParams],
  );

  const handleDisplayAnswer = useCallback(
    (question: string) => {
      setDisplayAnwer((d) => (d === question ? null : question));
    },
    [setDisplayAnwer],
  );

  return (
    <Stack
      gap={3}
      sx={{
        borderRadius: "10px",
        boxShadow: "0px 3px 17px 0px rgba(0, 0, 0, 0.10)",
        padding: isMobile ? "12.8px" : "18px",
        maxWidth: "100%",
      }}
    >
      <Stack direction="row" gap={2} alignSelf="center">
        {faqs.map((f) => (
          <Header
            key={f.section}
            section={section}
            f={f}
            handleSetSearchParam={handleSetSearchParam}
          />
        ))}
      </Stack>
      <Stack gap={1}>
        {faqs
          .filter((faq) => t(faq.param) === section)
          .map((f) => {
            const filteredFaqs =
              glp1WeightLossSubscription || glp1Charge
                ? f.faqs
                : f.faqs.filter((q) => !t(q.question).includes("GLP-1"));

            return filteredFaqs.map((q, idx) => (
              <Content
                key={idx}
                q={q}
                displayAnswer={displayAnswer}
                handleDisplayAnswer={handleDisplayAnswer}
              />
            ));
          })}
      </Stack>
    </Stack>
  );
};

export default FrequentlyAskedQuestions;
